import { Injectable } from '@angular/core';
import { StepOneModel } from '../../../shared/models/states/loan-step-one.model';
import { BrowserStorageService, StorageKeys } from '../../services/browser-storage.service';
import { LoanApplicationService } from '../../services/loan/loan-application.api.service';
import { lastValueFrom } from 'rxjs';
import { ApplicationListItemService } from '../../services/loan/application-list-items.service';
import { ApplicantService } from '../../services/loan/applicant.api.service';
import { StepTwoState } from './step-two-state';
import { ApplicantModel } from '../../../shared/models/states/applicant.model';

/** Mirrors primary Step 5 / loan_application fields; stored on applicant for next-app autofill. */
const SAVED_INDIVIDUAL_FINANCIAL_KEYS = [
    'salary_wages',
    'dividends_interest',
    'gross_rental_income',
    'net_business_income',
    'other_income',
    'federal_state_income_taxes',
    'property_taxes',
    'real_estate_loan_payment_taxes',
    'contract_note_payments',
    'other_expenses',
    'taxes',
    'law_suits',
    'other_liabilities',
] as const;

@Injectable({
    providedIn: 'root',
})
export class StepOneState {
    private _state?: StepOneModel;
    public _referenceNumber?: string;

    constructor(
        private s2: StepTwoState,
        private bss: BrowserStorageService,
        private loanApplicationService: LoanApplicationService,
        private listItemService: ApplicationListItemService,
        private applicantService: ApplicantService,
    ) { }

    get state() {
        if (!this._state) {
            const item = this.bss.getSessionItem(StorageKeys.StepOneModel);
            if (item) this._state = item;
            return item ?? undefined;
        }
        return this._state;
    }

    set state(value: StepOneModel | undefined) {
        this._state = value;
        this.bss.setSessionItem(StorageKeys.StepOneModel, this._state);
    }

    get referenceNumber() {
        if (!this._referenceNumber) {
            const item = this.bss.getSessionItem(StorageKeys.StepOneReferenceNumber);
            if (item) this._referenceNumber = item;
            return item ?? undefined;
        }
        return this._referenceNumber;
    }

    set referenceNumber(value: string | undefined) {
        this._referenceNumber = value;
        this.bss.setSessionItem(StorageKeys.StepOneReferenceNumber, value)
    }

    async syncDB(update: boolean) {
        // If there is no state, then create a new application
        if (!this._state) return;
        if (!update) {
            await this.createNew(this._state);
            this.fetchReferenceNumber();
        }
        else if (update) await this.update(this._state);
    }

    private async createNew(value: StepOneModel) {
        const loan = await lastValueFrom(this.loanApplicationService.createLoanApplication(value));
        if (!loan.uuid) {
            console.error("Could not create loan");
            return;
        }
        if (this._state) this._state.uuid = loan.uuid;
        await lastValueFrom(this.loanApplicationService.patchProgress(loan.uuid, { highest_completed_step: 0 }));
        const applicant = await lastValueFrom(this.applicantService.createApplicant(loan.uuid));
        this.mergeSavedIndividualFinancialsFromApplicant(applicant);
        this.s2.state = {
            loan_id: loan.uuid,
            applicant,
            isIndividualApplicant: this.s2.state?.isIndividualApplicant ?? true
        }
        this.bss.setSessionItem(StorageKeys.StepOneModel, this._state);
    }

    private async update(value: StepOneModel) {
        if (!value.uuid) return;
        const loan = await lastValueFrom(this.loanApplicationService.updateLoanApplication(value.uuid, value));
        if (!loan.uuid) {
            console.error("Could not update loan");
            return;
        }
        if (!this.s2.state?.applicant) {
            const applicant = await lastValueFrom(this.applicantService.createApplicant(loan.uuid));
            this.mergeSavedIndividualFinancialsFromApplicant(applicant);
            this.s2.state = {
                loan_id: loan.uuid,
                applicant,
                isIndividualApplicant: this.s2.state?.isIndividualApplicant ?? true
            }
        }
    }

    /** Copy last-saved Individual Account amounts from applicant onto the new loan when the loan has no values yet. */
    private mergeSavedIndividualFinancialsFromApplicant(applicant: ApplicantModel): void {
        if (!this._state) return;
        let changed = false;
        for (const k of SAVED_INDIVIDUAL_FINANCIAL_KEYS) {
            const v = (applicant as Record<string, unknown>)[k];
            const cur = (this._state as Record<string, unknown>)[k];
            if (v != null && cur == null) {
                (this._state as Record<string, unknown>)[k] = v;
                changed = true;
            }
        }
        if (changed) {
            this.bss.setSessionItem(StorageKeys.StepOneModel, this._state);
        }
    }

    private fetchReferenceNumber() {
        if (!this.referenceNumber && this.state?.uuid) {
            this.referenceNumber = this.state.uuid.substring(0, 8);
            // this.listItemService.getLoanApplicationId(this.state?.uuid).subscribe((la => {
            //   this.referenceNumber = la[0]?.reference ?? undefined;
            // }));
        }
    }

    get uuid() {
        return this.state?.uuid;
    }
    get location() {
        return this.state?.branch_location_uuid ?? '';
    }
    get businessType() {
        return this.state?.business_type ?? '';
    }
    get businessName() {
        return this.state?.business_name ?? '';
    }
    get loanAmount() {
        return this.state?.loan_amount;
    }
    get loanPurpose() {
        return this.state?.loan_purpose ?? '';
    }
}
